import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { WizardFormData, CustomerType } from '../create-customer-wizard-types';

interface Plan {
  id?: number;
  sku: string;
  type: string;
}

interface Reseller {
  id: string | number;
  name: string;
}

interface Props {
  watchedValues: WizardFormData;
  customerType: CustomerType;
  plans: Plan[];
  resellers: Reseller[];
}

export function CustomerWizardReview({
  watchedValues,
  customerType,
  plans,
  resellers
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const selectedPlan = plans.find(p => p.id === watchedValues.plan_id);
  const parentReseller = resellers.find(
    r => Number(r.id) === watchedValues.parent_organization_id
  );

  const sectionStyle = {
    borderColor: getColour('hairline'),
    backgroundColor: getColour('appSoft')
  };

  return (
    <div className="space-y-4">
      <p className="text-sm" style={{ color: getColour('inactiveText') }}>
        {t('review-customer-details')}
      </p>

      {/* Organization Info */}
      <div className="p-4 rounded-2xl border" style={sectionStyle}>
        <h4
          className="text-sm font-medium mb-3"
          style={{ color: getColour('normalText') }}
        >
          {t('organization-details')}
        </h4>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt style={{ color: getColour('inactiveText') }}>{t('type')}:</dt>
          <dd style={{ color: getColour('normalText') }}>
            {t(customerType === 'sub_org' ? 'sub-organization' : customerType)}
          </dd>
          {parentReseller && (
            <>
              <dt style={{ color: getColour('inactiveText') }}>
                {t('parent-reseller')}:
              </dt>
              <dd style={{ color: getColour('normalText') }}>
                {parentReseller.name}
              </dd>
            </>
          )}
          <dt style={{ color: getColour('inactiveText') }}>{t('name')}:</dt>
          <dd style={{ color: getColour('normalText') }}>
            {watchedValues.name}
          </dd>
          <dt style={{ color: getColour('inactiveText') }}>{t('email')}:</dt>
          <dd style={{ color: getColour('normalText') }}>
            {watchedValues.email}
          </dd>
          {watchedValues.phone && (
            <>
              <dt style={{ color: getColour('inactiveText') }}>
                {t('phone')}:
              </dt>
              <dd style={{ color: getColour('normalText') }}>
                {watchedValues.phone}
              </dd>
            </>
          )}
          <dt style={{ color: getColour('inactiveText') }}>
            {t('max-users')}:
          </dt>
          <dd style={{ color: getColour('normalText') }}>
            {watchedValues.max_users}
          </dd>
          <dt style={{ color: getColour('inactiveText') }}>{t('status')}:</dt>
          <dd style={{ color: getColour('normalText') }}>
            {watchedValues.is_active ? t('active') : t('inactive')}
          </dd>
        </dl>
      </div>

      {/* Plan */}
      <div className="p-4 rounded-2xl border" style={sectionStyle}>
        <h4
          className="text-sm font-medium mb-3"
          style={{ color: getColour('normalText') }}
        >
          {t('plan-subscription')}
        </h4>
        {selectedPlan ? (
          <div>
            <p className="text-sm" style={{ color: getColour('normalText') }}>
              {selectedPlan.sku} ({selectedPlan.type})
            </p>
            <p
              className="text-xs mt-1"
              style={{ color: getColour('inactiveText') }}
            >
              {watchedValues.auto_renew
                ? t('auto-renew-enabled')
                : t('auto-renew-disabled')}
            </p>
          </div>
        ) : (
          <p className="text-sm" style={{ color: getColour('inactiveText') }}>
            {t('no-plan-selected')}
          </p>
        )}
      </div>

      {/* Users */}
      <div className="p-4 rounded-2xl border" style={sectionStyle}>
        <h4
          className="text-sm font-medium mb-3"
          style={{ color: getColour('normalText') }}
        >
          {t('users')} ({watchedValues.users?.length || 0})
        </h4>
        {watchedValues.users && watchedValues.users.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {watchedValues.users.map((user, i) => (
              <li key={i} style={{ color: getColour('normalText') }}>
                {user.first_name} {user.last_name} ({user.email})
                {user.roles.length > 0 && (
                  <span
                    className="text-xs ml-1"
                    style={{ color: getColour('inactiveText') }}
                  >
                    - {user.roles.join(', ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm" style={{ color: getColour('inactiveText') }}>
            {t('no-users-added')}
          </p>
        )}
      </div>

      {/* Sub-organizations (reseller only) */}
      {watchedValues.is_reseller && (
        <div className="p-4 rounded-2xl border" style={sectionStyle}>
          <h4
            className="text-sm font-medium mb-3"
            style={{ color: getColour('normalText') }}
          >
            {t('sub-organizations')} (
            {watchedValues.sub_organizations?.length || 0})
          </h4>
          {watchedValues.sub_organizations &&
          watchedValues.sub_organizations.length > 0 ? (
            <ul className="space-y-1 text-sm">
              {watchedValues.sub_organizations.map((subOrg, i) => (
                <li key={i} style={{ color: getColour('normalText') }}>
                  {subOrg.name} ({subOrg.email})
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm" style={{ color: getColour('inactiveText') }}>
              {t('no-sub-organizations-added')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
